/**
 * PTEL Tool - EditorRequestForm (base)
 * Modal para solicitar acceso como Editor
 *
 * @module components/auth/EditorRequestForm
 * @version 1.0.0
 * @task FB.10.11a — estructura + validación + submit
 */

import type { CSSProperties } from 'react';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Send, X, Loader2, CheckCircle2, AlertCircle, Clock } from 'lucide-react';

import type { Theme } from './auth.types';
import { getAuthTokens, getButtonStyles, getCardStyles, getAlertStyles } from './auth.tokens';
import { useAuth } from '../../contexts/AuthContext';
import { useAudit } from '../../hooks/useAudit';
import { useIsMounted } from '../../hooks/useIsMounted';
import { createRequest, getUserPendingRequest } from '../../services/firebase/editorRequest.service';
import { sendEditorRequestNotification } from '../../services/email/emailNotification.service';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface EditorRequestFormProps {
  open: boolean;
  onClose: () => void;
  theme: Theme;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const MIN_REASON_LENGTH = 10;
const MAX_REASON_LENGTH = 500;

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function EditorRequestForm({ open, onClose, theme }: EditorRequestFormProps) {
  const { userData } = useAuth();
  const { logDocument } = useAudit();
  const isMounted = useIsMounted();
  const t = getAuthTokens(theme);
  const primaryBtn = getButtonStyles(theme, 'primary');
  const secondaryBtn = getButtonStyles(theme, 'secondary');
  const cardStyles = getCardStyles(theme);
  const successAlert = getAlertStyles(theme, 'success');
  const errorAlert = getAlertStyles(theme, 'error');

  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<{ requestedAt: Date } | null>(null);
  const [isCheckingPending, setIsCheckingPending] = useState(false);

  // SSCA-FIX-1 (SF1D · DOP-SF1D-TIMER): referencia al temporizador de éxito
  // para poder cancelarlo en el desmontaje del modal.
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isValid = reason.trim().length >= MIN_REASON_LENGTH;
  const charsLeft = MAX_REASON_LENGTH - reason.length;

  // ---------------------------------------------------------------------------
  // Check pending request on open
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!open || !userData) return;
    let cancelled = false;

    setIsCheckingPending(true);
    getUserPendingRequest(userData.uid)
      .then((result) => {
        if (!cancelled) setPendingRequest(result);
      })
      .catch(() => {
        /* silencioso */
      })
      .finally(() => {
        if (!cancelled) setIsCheckingPending(false);
      });

    return () => { cancelled = true; };
  }, [open, userData]);

  // ---------------------------------------------------------------------------
  // Cleanup del temporizador de éxito (SSCA-FIX-1 · SF1D · DOP-SF1D-TIMER)
  // ---------------------------------------------------------------------------

  useEffect(() => () => {
    if (successTimerRef.current !== null) {
      clearTimeout(successTimerRef.current);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = useCallback(async () => {
    if (!userData || !isValid || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await createRequest(
        { uid: userData.uid, email: userData.email, displayName: userData.displayName },
        reason.trim(),
      );

      // Audit: solicitud editor creada
      void logDocument('create', `editor-request-${userData.uid}`, '', null, {
        requesterEmail: userData.email,
        reason: reason.trim().slice(0, 100),
      });

      // Notificación de email (Worker + Resend) — silenciosa si falla
      try {
        await sendEditorRequestNotification({
          requesterName: userData.displayName,
          requesterEmail: userData.email,
          reason: reason.trim(),
        });
      } catch {
        /* no bloquea éxito */
      }

      // SSCA-FIX-1 (SF1D): guard de montaje — no agendar setState ni el
      // temporizador de éxito si el componente se desmontó durante el await.
      if (isMounted()) {
        setSubmitSuccess(true);
        successTimerRef.current = setTimeout(() => {
          setReason('');
          setSubmitSuccess(false);
          onClose();
        }, 2000);
      }
    } catch (err) {
      if (isMounted()) {
        setSubmitError(err instanceof Error ? err.message : 'Error al enviar solicitud');
      }
    } finally {
      if (isMounted()) {
        setIsSubmitting(false);
      }
    }
  }, [userData, reason, isValid, isSubmitting, onClose, logDocument, isMounted]);

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------

  const styles = useMemo((): Record<string, CSSProperties> => ({
    backdrop: {
      position: 'fixed',
      inset: 0,
      background: t.backdrop,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
    },
    modal: {
      ...cardStyles.container,
      width: '100%',
      maxWidth: '440px',
      margin: '16px',
      position: 'relative' as const,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '20px',
    },
    title: {
      fontSize: '18px',
      fontWeight: 700,
      color: t.text,
      fontFamily: "'Space Grotesk', sans-serif",
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      color: t.textMuted,
      cursor: 'pointer',
      padding: '4px',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '14px',
      fontWeight: 500,
      color: t.textSecondary,
      fontFamily: "'Space Grotesk', sans-serif",
    },
    textarea: {
      width: '100%',
      minHeight: '100px',
      padding: '10px 12px',
      background: t.bgInput,
      border: `1px solid ${t.border}`,
      borderRadius: '8px',
      color: t.text,
      fontSize: '14px',
      fontFamily: "'JetBrains Mono', monospace",
      resize: 'vertical' as const,
      outline: 'none',
      boxSizing: 'border-box' as const,
    },
    counter: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '6px',
      fontSize: '12px',
      color: charsLeft < 50 ? t.error : t.textMuted,
      fontFamily: "'JetBrains Mono', monospace",
    },
    hint: {
      fontSize: '12px',
      color: t.textMuted,
    },
    actions: {
      display: 'flex',
      gap: '10px',
      marginTop: '20px',
    },
    submitBtn: {
      ...primaryBtn.base,
      flex: 1,
      ...(!isValid || isSubmitting ? primaryBtn.disabled : {}),
    },
    cancelBtn: {
      ...secondaryBtn.base,
      width: 'auto',
      padding: '10px 20px',
    },
  }), [t, cardStyles, primaryBtn, secondaryBtn, charsLeft, isValid, isSubmitting]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!open) return null;

  return (
    <div style={styles.backdrop} data-testid="editor-request-backdrop" onClick={onClose}>
      <div
        style={styles.modal}
        data-testid="editor-request-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Solicitar acceso Editor"
      >
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>Solicitar acceso Editor</span>
          <button
            type="button"
            style={styles.closeBtn}
            onClick={onClose}
            data-testid="editor-request-close"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Loading: verificando solicitud pendiente */}
        {isCheckingPending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '20px 0' }} data-testid="editor-request-checking">
            <Loader2 size={18} style={{ color: t.textMuted, animation: 'spin 1s linear infinite' }} />
            <span style={{ color: t.textMuted, fontSize: '14px' }}>Verificando solicitud...</span>
          </div>
        )}

        {/* Pending state: solicitud ya existente */}
        {!isCheckingPending && pendingRequest && (
          <div data-testid="editor-request-pending" style={{ textAlign: 'center', padding: '16px 0' }}>
            <Clock size={32} style={{ color: t.textMuted, margin: '0 auto 12px' }} />
            <p style={{ color: t.text, fontSize: '15px', fontWeight: 600, marginBottom: '6px' }}>
              Solicitud pendiente
            </p>
            <p style={{ color: t.textMuted, fontSize: '13px', marginBottom: '20px' }}>
              Enviada el{' '}
              {pendingRequest.requestedAt.toLocaleDateString('es-ES', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}
            </p>
            <button type="button" style={styles.cancelBtn} onClick={onClose}>
              Cerrar
            </button>
          </div>
        )}

        {/* Success state */}
        {submitSuccess && (
          <div style={successAlert.container} data-testid="editor-request-success">
            <CheckCircle2 size={16} style={successAlert.icon} />
            <span style={successAlert.text}>
              Solicitud enviada. Un administrador la revisará.
            </span>
          </div>
        )}

        {/* Error state */}
        {submitError && (
          <div style={errorAlert.container} data-testid="editor-request-error">
            <AlertCircle size={16} style={errorAlert.icon} />
            <span style={errorAlert.text}>{submitError}</span>
          </div>
        )}

        {/* Form — solo si no hay pendiente y no estamos verificando */}
        {!submitSuccess && !pendingRequest && !isCheckingPending && (
          <>
            <label style={styles.label} htmlFor="editor-request-reason">
              Justificación
            </label>
            <textarea
              id="editor-request-reason"
              style={styles.textarea}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
              placeholder="Explica por qué necesitas acceso Editor (mín. 10 caracteres)..."
              disabled={isSubmitting}
              data-testid="editor-request-textarea"
            />
            <div style={styles.counter}>
              <span style={styles.hint}>
                {reason.trim().length < MIN_REASON_LENGTH
                  ? `Mínimo ${MIN_REASON_LENGTH - reason.trim().length} caracteres más`
                  : 'Listo para enviar'}
              </span>
              <span>{charsLeft} / {MAX_REASON_LENGTH}</span>
            </div>

            <div style={styles.actions}>
              <button
                type="button"
                style={styles.cancelBtn}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                style={styles.submitBtn}
                onClick={handleSubmit}
                disabled={!isValid || isSubmitting}
                data-testid="editor-request-submit"
              >
                {isSubmitting ? (
                  <><Loader2 size={16} className="animate-spin" /> Enviando...</>
                ) : (
                  <><Send size={16} /> Enviar solicitud</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
